/**
 * Map a grade result + a CONFIRMED catalog identity into a `CollectionItemCreate` payload.
 * BETA-COMMERCE-298.
 *
 * Pure and separately testable on purpose: this is the exact seam where the grade-to-vault save
 * has silently lost data before, and a seam you cannot assert on is a seam that rots.
 *
 * ⛔⛔ THE FIELD NAMES ARE THE CONTRACT, AND THE WRONG ONES USED TO BE ACCEPTED SILENTLY.
 * `CollectionItemBase` was Pydantic-default `extra="ignore"`, so a payload of `{product_id, grade,
 * acquisition_price, source}` produced a 201 with every one of those keys DROPPED — an unlinked,
 * ungraded blank row in someone's collection, reported as success. `extra="forbid"` now makes that
 * a 422, and this function emits the real names:
 *     comic_issue_id · user_grade (string, max 10) · grade_confidence (int 0-100) · purchase_price
 *
 * ⛔ WE ALSO WRITE title/issue_number/publisher/year, AND THAT IS NOT REDUNDANCY.
 * `GET /vault/items` builds `CollectionItemResponse`, which reads the item's OWN columns and joins
 * NOTHING from the linked issue (only the single-item detail route hydrates `issue_title`). Saving
 * `comic_issue_id` alone therefore yields a correctly-linked row that renders as **"Untitled"** in
 * the collection list. The operator's acceptance is "appears in the Vault with grade AND title", so
 * the identity is denormalized at write time.
 */

/**
 * ⛔⛔ A MOCK GRADE MUST NEVER BE WRITTEN AS A GRADE.
 *
 * `isRealGrade()` is false when the backend stamped `is_mock` / `ai_grade_engine: "mock"` — i.e.
 * the model was unavailable and the number is a fallback estimate, not a measurement. Persisting it
 * into `user_grade` would launder an admitted placeholder into a durable, badge-rendered grade on
 * the user's own book, which is precisely the fabricated-9.8 failure this codebase has already
 * lived through. The book still saves; the NUMBER does not.
 */
export const MOCK_GRADE_OMITTED = 'mock_grade_omitted';

/**
 * The identity half of a vault write, shared by BOTH writers into `POST /vault/items`
 * (the grade flow's Save, and QuickAdd's Add to Vault).
 *
 * ⛔ ONE FUNCTION ON PURPOSE. The two callers drifted apart once already: QuickAdd was posting
 * `{product_id, grade, acquisition_price, source}` — four keys `CollectionItemCreate` does not have
 * — so every Add to Vault 422'd under extra="forbid", and before that returned 201 having silently
 * dropped all four. Two writers, one endpoint, one mapping.
 *
 * @param {Object} issue  a catalog issue the USER confirmed
 * @returns {Object} the identity fields of a CollectionItemCreate payload
 */
export function buildVaultIdentity(issue) {
  if (!issue || issue.id === null || issue.id === undefined) {
    throw new Error('buildVaultIdentity requires a user-confirmed issue');
  }
  return {
    comic_issue_id: issue.id,
    // ⛔ Denormalized DELIBERATELY. `GET /vault/items` builds CollectionItemResponse from the
    // item's OWN columns and joins nothing from the linked issue, so a link-only save renders as
    // "Untitled" in the collection list — correctly linked in the database, invisible to the user.
    title: issue.series_name || issue.title || null,
    issue_number: issue.number ?? null,
    publisher: issue.publisher || issue.publisher_name || null,
    year: parseCoverYear(issue.cover_date),
    // ⛔ is_graded means SLABBED BY A THIRD PARTY (it pairs with grading_company/cert_number to
    // render "CGC 9.4"). Neither writer collects a company or a cert number, so neither may claim
    // certification: a self-assessed or AI-estimated grade is not a slab, and marking it graded
    // would put certified books and guesses in the same filter.
    is_graded: false,
  };
}

/**
 * @param {Object} params
 * @param {Object} params.result   grade result from the grading flow
 * @param {Object} params.issue    the issue the USER selected and confirmed
 * @param {boolean} params.realGrade  isRealGrade(result)
 * @returns {{payload: Object, omitted: string[]}}
 */
export function buildVaultItemFromGrade({ result, issue, realGrade }) {
  const omitted = [];
  const payload = buildVaultIdentity(issue);

  const grade = result?.grade ?? result?.estimated_grade ?? null;
  if (!realGrade) {
    omitted.push(MOCK_GRADE_OMITTED);
  } else if (grade !== null && grade !== undefined && grade !== '') {
    // max_length=10 on the column; a numeric grade is at most "10.0".
    payload.user_grade = String(grade).slice(0, 10);
    const confidence = normalizeConfidence(result?.confidence);
    if (confidence !== null) payload.grade_confidence = confidence;
  }

  return { payload, omitted };
}

/**
 * "1988-05-01" -> 1988. Tolerates a bare year and null.
 *
 * ⚠ Parsed off the STRING, not via `new Date(...)`: `new Date('1988-05-01').getFullYear()` is
 * local-time and returns 1987 for anyone west of UTC. A cover year that silently shifts by one is
 * both wrong and nearly invisible.
 */
export function parseCoverYear(coverDate) {
  if (!coverDate) return null;
  const match = /^(\d{4})/.exec(String(coverDate));
  if (!match) return null;
  return parseInt(match[1], 10);
}

/**
 * The API takes `grade_confidence` as an int 0-100 (ge=0, le=100) but the grading flow reports
 * confidence as a 0-1 fraction. Sending 0.87 would store **0**, quietly recording high confidence
 * as none; sending 87.4 would 422. Scale, round, clamp.
 */
export function normalizeConfidence(confidence) {
  if (typeof confidence !== 'number' || Number.isNaN(confidence)) return null;
  const scaled = confidence <= 1 ? confidence * 100 : confidence;
  return Math.max(0, Math.min(100, Math.round(scaled)));
}

export default {
  buildVaultItemFromGrade,
  buildVaultIdentity,
  parseCoverYear,
  normalizeConfidence,
  MOCK_GRADE_OMITTED,
};
